/****************************************************
 * Module Name:  MineMaster.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import { Box, Tooltip, Button } from "@mui/material";
import NoPermission from "../../common/noPermission";
import React, { useState, useEffect } from "react";
import Constants from "../../../utility/constants";
import MineProxy from "../../../utility/proxy/manager/manufacturingProxy/mineProxy";
import CommonSnackbar from "../../common/commonSnackBar";
import { DataGridPro, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import Utility from "../../../utility/utility";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import AddMineModal from "./addMineModal";
import PageTitle from "../../common/pageTitle";

const MineMaster = () => {
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [mineListLoader, setMineListLoader] = useState(false);
    const [mineList, setMineList] = useState([]);
    const [columnName, setColumnName] = useState([]);
    const [access, setAccess] = useState(false);
    const [showAddMineModal, setShowAddMineModal] = useState(false);
    const [editOperation, setEditOperation] = useState(false);
    const [sourceData, setSourceData] = useState({});
    const [loadingButton, setLoadingButton] = useState(false);
    const [mineSuccessSnackbar, setMineSuccessSnackbar] = useState(false);
    const [mineOperation, setMineOperation] = useState('create');

    // eslint-disable-next-line
    const [local_Permission, setLocal_Permission] = useState(
        Constants.USER_PERMISSION.filter(
            (iPermission) =>
                iPermission.permissionid === Constants.PERMISSIONS.TENDER_MASTER_MINE
        )
    );

    useEffect(() => {
        PageTitle("TENDER_MASTER_MINE");
        checkPermission();
    }, []);

    const checkPermission = () => {
        let allowed = Constants.USER_PERMISSION.find(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.TENDER_MASTER_MINE
        ) !== undefined
        if (!allowed) {
            setAccess(true)
            return <NoPermission />
        } else {
            fetchMineData(true);
        }
    }

    const fetchMineData = (bAnimateSpinner) => {
        setMineListLoader(true);
        bAnimateSpinner && setMineListLoader(true);
        MineProxy.GetAllMines(
            (responseData) => {
                if (responseData) {
                    const uniqueFilterValues = {
                        name: Utility.getDistinctOptions(responseData, "name"),
                        pocname: Utility.getDistinctOptions(responseData, "pocname"),
                        country: Utility.getDistinctOptions(responseData, "country"),
                        state: Utility.getDistinctOptions(responseData, "state"),
                        city: Utility.getDistinctOptions(responseData, "city"),
                        pincode: Utility.getDistinctOptions(responseData, "pincode"),
                    };
                    setMineList(responseData);
                    setMineListLoader(false);
                    prepareColumns(uniqueFilterValues);
                } else {
                    setErrorSnackbar(true);
                    setMineListLoader(false);
                }
            }
        )
    };

    const prepareColumns = (uniqueFilterValues) => {
        let columnNames = [];

        columnNames.push(
            {
                field: "id",
                headerName: "ID",
                headerAlign: "center",
                align: "center",
                type: "number",
                valueFormatter: (params) => params.value,
                width: 100,
            },
        );

        Constants.USER_COLUMN_PERMISSION.forEach(iColPermission => {
            if (columnsData.hasOwnProperty(iColPermission)) {
                const columnData = columnsData[iColPermission];
                if (columnData.field === "pocnumber") {
                    columnNames.push({
                        field: "pocphonecode",
                        headerName: "PHONECODE",
                        headerAlign: "center",
                        align: "center",
                        width: 100,
                    });
                }
                if (columnData.field === "country") {
                    columnData.type = "singleSelect";
                    columnData.valueOptions = uniqueFilterValues ? uniqueFilterValues.country : [];
                    columnData.filterOperators = Utility.combinedFilters();
                }
                if (columnData.field === "name") {
                    columnData.type = "singleSelect";
                    columnData.valueOptions = uniqueFilterValues ? uniqueFilterValues.name : [];
                    columnData.filterOperators = Utility.combinedFilters();
                }
                if (columnData.field === "pocname") {
                    columnData.type = "singleSelect";
                    columnData.valueOptions = uniqueFilterValues ? uniqueFilterValues.pocname : [];
                    columnData.filterOperators = Utility.combinedFilters();
                }
                if (columnData.field === "state") {
                    columnData.type = "singleSelect";
                    columnData.valueOptions = uniqueFilterValues ? uniqueFilterValues.state : [];
                    columnData.filterOperators = Utility.combinedFilters();
                }
                if (columnData.field === "city") {
                    columnData.type = "singleSelect";
                    columnData.valueOptions = uniqueFilterValues ? uniqueFilterValues.city : [];
                    columnData.filterOperators = Utility.combinedFilters();
                }
                if (columnData.field === "pincode") {
                    columnData.type = "singleSelect";
                    columnData.valueOptions = uniqueFilterValues ? uniqueFilterValues.pincode : [];
                    columnData.filterOperators = Utility.combinedFilters();
                }
                columnNames.push(columnData);
            }
        });

        columnNames.push({
            field: "edit",
            headerName: "EDIT",
            headerAlign: "center",
            align: "center",
            width: 100,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (
                    <Box
                        style={{ textAlign: "center", cursor: "pointer" }}
                        onClick={() => {
                            setEditOperation(true)
                            setShowAddMineModal(true)
                            setSourceData(params.row)
                        }}
                    >
                        {" "}
                        <BorderColorIcon color="primary" />
                    </Box>
                );
            },
        });
        setColumnName(columnNames);
    }

    const addOrUpdateMineData = (mineData) => {
        setLoadingButton(true);
        setMineOperation(editOperation ? 'update' : 'create');
        if (editOperation) {
            MineProxy.UpdateMine(mineData, (response) => {
                setLoadingButton(false);
                if (response) {
                    fetchMineData(true);
                    setShowAddMineModal(false);
                    setMineListLoader(false);
                    setMineSuccessSnackbar(true);
                }
                else {
                    setErrorSnackbar(true);
                    setMineListLoader(false);
                }
            });
        } else {
            MineProxy.AddMine(mineData, (response) => {
                setLoadingButton(false);
                if (response) {
                    fetchMineData(true);
                    setShowAddMineModal(false);
                    setMineListLoader(false);
                    setMineSuccessSnackbar(true);
                }
                else {
                    setErrorSnackbar(true);
                    setMineListLoader(false);
                }
            });
        }
    }

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                <Box sx={{ flexGrow: 1, display: "block" }} />
                {mineListLoader ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            color="primary"
                            fontSize="medium"
                            onClick={() => fetchMineData(true)}
                            sx={{ cursor: "pointer" }}
                        />
                    </Tooltip>
                )}
                {Utility.isAddAllowed(local_Permission) ? (
                    <Tooltip title="Add Mine">
                        <Button
                            color="info"
                            variant="outlined"
                            size="small"
                            sx={{ ml: "5px" }}
                            onClick={() => {
                                setShowAddMineModal(true);
                                setEditOperation(false);
                                setSourceData({});
                            }
                            }
                            startIcon={<AddBoxOutlinedIcon size="large" />}
                        >
                            Mine
                        </Button>
                    </Tooltip>
                ) : null}
                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        )
    }


    const handleClose = () => {
        setMineSuccessSnackbar(false);
    };

    const handleValidation = (criteriaData) => {
        //TODO do validation if needed here
        return true;
    };

    const columnsData = {
        "4a1de405-1802-4664-85e2-6ff7cf8c3bba": {
            field: "name",
            headerName: "NAME",
            headerAlign: "center",
            align: "center",
            width: 200,
        },
        "ee64ed64-1a9e-4f9e-bf6b-22ef8535edde": {
            field: "pocname",
            headerName: "POCNAME",
            headerAlign: "center",
            align: "center",
            width: 150,
        },
        "8200ae89-ef23-471c-9a82-d8fb276bc3b5": {
            field: "pocnumber",
            headerName: "POCNUMBER",
            headerAlign: "center",
            align: "center",
            width: 200,
        },
        "2593e023-ac63-477d-ad43-a881f05aad99": {
            field: "address1",
            headerName: "ADDRESS1",
            headerAlign: "center",
            align: "center",
            width: 200,
        },
        "1015ecdc-7e78-4f4a-9ad5-8a959d4b84fe": {
            field: "address2",
            headerName: "ADDRESS2",
            headerAlign: "center",
            align: "center",
            width: 200,
        },
        "309c938a-d99c-4353-8f5d-02b6239967f7": {
            field: "country",
            headerName: "COUNTRY",
            headerAlign: "center",
            align: "center",
            width: 200,
        },
        "38b02937-b1b4-4f18-8bba-fdddc7241e5c": {
            field: "state",
            headerName: "STATE",
            headerAlign: "center",
            align: "center",
            width: 200,
        },
        "7bbe5665-be49-4dd5-ac07-3f9cddef92d9": {
            field: "city",
            headerName: "CITY",
            headerAlign: "center",
            align: "center",
            width: 200,
        },
        "bad7b839-9bff-4bae-b317-a4b362ab5232": {
            field: "pincode",
            headerName: "PINCODE",
            headerAlign: "center",
            align: "center",
            width: 150,
        },
    };

    const columns = Utility.isUpdateAllowed(local_Permission)
        ? columnName
        : columnName.filter((col) => col.field !== "edit");

    return (
        <Box>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />
            {mineSuccessSnackbar ? (
                <CommonSnackbar
                    show={mineSuccessSnackbar}
                    message={`Mine ${mineOperation === 'update' ? 'updated' : 'created'} successfully`}
                    variant={"success"}
                    autoHide={true}
                    title={"success"}
                    handleClose={handleClose}
                />
            ) : null}
            {showAddMineModal ? (
                <AddMineModal
                    Show={true}
                    handleClose={() => setShowAddMineModal(false)}
                    handleSave={(data) => addOrUpdateMineData(data)}
                    handleValidation={() => handleValidation()}
                    editOperation={editOperation}
                    sourceData={sourceData}
                    loading={loadingButton}
                />
            ) : null}
            {
                access ? <NoPermission /> : (
                    <Box px={1}>
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                color: '#000',
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                height: "83vh",
                                "& .lavenderCell": {
                                    color: "#080808",
                                    backgroundColor: "#D8BFD8",
                                },
                            }}
                        >
                            <DataGridPro
                                disableRowSelectionOnClick={true}
                                getRowHeight={() => "auto"}
                                density="compact"
                                loading={mineListLoader}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "4px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "18px",
                                    },

                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                                slots={{ toolbar: CustomToolbar }}
                                rows={mineList}
                                columns={columns}
                                getRowId={(row) => row.id}
                                rowSpacingType="border"
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns status and traderName, the other columns will remain visible
                                            id: false,
                                        },
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 },
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                )
            }
        </Box>
    );
};


export default MineMaster;